<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-red" sprite="atoms" />
          Omnia Themes
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-red" sprite="atoms" />
            Omnia Themes
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>

      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
          <el-button class="ml-10 hidden-xs-only" @click="doExport">
            Export
          </el-button>
        </div>
        <div class="box right">
          <el-button @click="openCreateModal" v-if="!isPartnerAdmin">Add New</el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading || deleteProcessing"
          :columns="tableColumns"
        />
      </div>
    </div>

    <create-form
      :createVisible="createVisible"
      :clearFormFields="clearFormFields"
      :createProcessing="createProcessing"
      @createOperation="createOperation"
      @cancelOperation="cancelOperation"
      :partnerOptions="partnerOptions"
    />

    <edit-form
      :editVisible="editVisible"
      :clearFormFields="clearFormFields"
      :editProcessing="editProcessing"
      :selectedOmniaTheme="selectedOmniaTheme"
      @editOperation="editOperation"
      @cancelOperation="cancelOperation"
      :partnerOptions="partnerOptions"
    />
  </div>
</template>

<script>
import feathersClient, { hostServer } from "../../../feathers/feathers-client";
import CreateForm from "./components/createForm";
import EditForm from "./components/editForm";
import { debounce } from "lodash";
import { globalEventBus } from "../../../globalEventBus";
import { ExportToCsv } from "export-to-csv";

export default {
  name: "OmniaThemesTable",

  data() {
    return {
      eventChannel: null,
      createProcessing: false,
      editProcessing: false,
      deleteProcessing: false,
      tableLoading: false,
      tableData: [],
      allOmniaThemes: [],
      search: "",
      visibleActionsId: "",
      editVisible: false,
      createVisible: false,
      clearFormFields: false,
      selectedOmniaTheme: null,
      total: 0,
      partnerOptions: [],
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-red" sprite="atoms" />
                </div>
              ];
            }
          }
        },
        {
          field: "name",
          title: "Name",
          sortable: true,
          minWidth: 150
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  {
                    !this.isPartnerAdmin &&
                    <el-button
                      id={"delete-" + row.id}
                      icon="el-icon-delete"
                      onClick={() => this.deleteOperation(row.id)}
                      class="table-action-button icon-red"
                    />
                  }
                </div>
              ];
            }
          }
        }
      ]
    };
  },

  components: {
    CreateForm,
    EditForm
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    user() {
      return this.$store.state.auth.user;
    },

    isPartnerAdmin() {
      if (this.user) {
        return +this.user.userRole === 11;
      }
    }
  },

  watch: {
    user() {
      this.fetchOmniaThemes();
      if (!this.eventChannel) {
        this.joinSocketChannel();
      }
    },

    search: debounce(function() {
      this.searchOmniaThemes();
    }, 250)
  },

  methods: {

    doExport() {
      const data = [...this.tableData];
      if (data.length) {
        const writeData = data.map(item => ({
          name: item.name
        }));

        const options = {
          filename: "omnia_themes",
          showLabels: true,
          headers: ["Name"]
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    searchOmniaThemes() {
      if (this.search.length) {
        this.tableData = this.tableData.filter(
          data =>
            !!data.name &&
            data.name.toLowerCase().includes(this.search.toLowerCase())
        );
      } else {
        this.tableData = [...this.allOmniaThemes];
      }
      this.total = this.tableData.length;
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    openCreateModal() {
      this.createVisible = true;
      this.clearFormFields = false;
    },

    openEditModal(id) {
      this.clearFormFields = false;
      this.tableLoading = true;
      feathersClient
        .service("omnia-themes")
        .get(id)
        .then(res => {
          this.selectedOmniaTheme = res;
          this.editVisible = true;
          this.tableLoading = false;
        });
    },

    createOperation(formData) {
      this.$confirm("Create new theme?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.createProcessing = true;
          fetch(`${hostServer}/omnia-themes`, {
            method: "POST",
            body: formData,
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`
            }
          })
            .then(res => res.json())
            .then(res => {
              // ⭐️ Throw Bad Request ⭐️ //
              if (res.name === "BadRequest") {
                throw res;
              }

              this.$notify({
                title: "Success",
                message: "Created successfully!",
                type: "success"
              });
              this.createProcessing = false;
              this.clearFormFields = true;
              this.cancelOperation("create");
            })
            .catch(error => {
              this.clearFormFields = false;

              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.createProcessing = false;
            });
        })
        .catch(() => true);
    },

    editOperation(editBody) {
      this.$confirm("Process omnia theme edit?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.editProcessing = true;

          fetch(`${hostServer}/omnia-themes/${this.selectedOmniaTheme.id}`, {
            method: "PATCH",
            body: editBody,
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`
            }
          })
            .then(res => res.json())
            .then(res => {
              // ⭐️ Throw BadRequest ⭐️ //
              if (res.name === "BadRequest" || res.name === "Forbidden") {
                throw res;
              }

              this.$notify({
                title: "Success",
                message: "Edited Successfully!",
                type: "success"
              });
              this.editProcessing = false;

              this.clearFormFields = true;
              this.cancelOperation("edit");
            })
            .catch(error => {
              this.clearFormFields = false;

              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.editProcessing = false;
            });
        })
        .catch(() => true);
    },

    cancelOperation(type) {
      this.clearFormFields = true;
      if (type === "create") {
        this.createVisible = false;
      } else if (type === "edit") {
        this.editVisible = false;
      }
    },

    deleteOperation(id) {
      this.$confirm("Process omnia theme delete?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.deleteProcessing = true;

          feathersClient.service('omnia-themes').remove(id)
            .then(() => {
              this.deleteProcessing = false;
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success"
              });
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.deleteProcessing = false;
            });
        })
        .catch(() => true);
    },

    fetchOmniaThemes() {
      this.tableLoading = true;
      feathersClient.service('omnia-themes').find().then(res => {
        this.tableData = [...res];
        this.tableLoading = false;
        this.total = res.length;
        this.allOmniaThemes = [...res];
      });
    },

    joinSocketChannel(){
      const channelService = feathersClient.service('channel-memberships');
      const data = {
        roomType: 'global-admins'
      }
      channelService.create(data).then(res => {this.eventChannel = 'global-admins';});
    },

    removeListeners() {
      feathersClient.service("omnia-themes").removeListener("patched");
      feathersClient.service("omnia-themes").removeListener("created");
      feathersClient.service("omnia-themes").removeListener("removed");
      globalEventBus.$off("appReconnected");
    },

    getPartnerOptions() {
      if(+this.user.userRole === 1) {
        feathersClient.service('partners').find({
          query: {
            type: "WHITELABEL",
            $sort: {
              name: 1
            }
          }
        }).then( (res) => {
          console.log(res);
          this.partnerOptions = [...res];
        });
      }
    }
  },

  created() {
    //event listeners
    //monitor socket connection and reconnect to channel
    globalEventBus.$on("appReconnected", () => {
      this.joinSocketChannel();
    });

    //record was edited
    //always refresh did destination options
    feathersClient.service("omnia-themes").on("patched", data => {

      const { userRole, partnerId } = this.user;
      if(+userRole === 11 && !data.resellerIds.includes(partnerId) ) {
        return;
      }

      const updateIndex = this.tableData.findIndex(item => item.id === data.id);
      const updateAllIndex = this.allOmniaThemes.findIndex(
        item => item.id === data.id
      );
      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1, data);
      }
      if (updateAllIndex >= 0) {
        this.allOmniaThemes.splice(updateAllIndex, 1, data);
      }
    });

    //record was created
    feathersClient.service("omnia-themes").on("created", data => {

      const { userRole, partnerId } = this.user;
      if(+userRole === 11 && !data.resellerIds.includes(partnerId) ) {
        return;
      }

      this.tableData.push(data);
      this.allOmniaThemes.push(data);
    });

    //record was deleted
    feathersClient.service("omnia-themes").on("removed", data => {
      const indexToRemove = this.tableData.findIndex(
        item => item.id === data.id
      );
      const indexAllToRemove = this.allOmniaThemes.findIndex(
        item => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allOmniaThemes.splice(indexAllToRemove, 1);
      }
    });
  },

  mounted() {
    if (this.user) {
      this.joinSocketChannel();
      this.fetchOmniaThemes();
      this.getPartnerOptions();
    }
  },

  beforeDestroy() {
    feathersClient
      .service("channel-memberships")
      .remove(null, { query: { channelName: this.eventChannel } });
    this.removeListeners();
  }
};
</script>
